import { ComponentType } from 'react';
import { BaseLoadingSpinner, RouteAuthenticationRequirement, RouteItem, useFeatureFlags } from '@invara-dev/react-base';
import { Box } from '../components/ui/chakraPrimitives';
import { Layout } from '../components/layout/layout.tsx';

// Factory function to create a legacy iframe component with a specific path
export const createLegacyComponent = (legacyPath: string, isNewApp: boolean | undefined) => {
  return async () => {
    const { LegacyIframe } = await import('../components/common/LegacyIframe');
    return {
      Component: () => <LegacyIframe legacyPath={legacyPath} isNewApp={isNewApp} />
    };
  };
};

export const createForcedLegacyComponent = (legacyPath: string, isNewApp: boolean | undefined) => {
  return async () => {
    const { LegacyIframe } = await import('../components/common/LegacyIframe');
    return {
      Component: () => <LegacyIframe legacyPath={legacyPath} isNewApp={isNewApp} forceLegacyMode />
    };
  };
};

// Component that gates rendering between a React component and the legacy iframe
// based on a feature flag, with optional inverted behavior for rollback flags.
// While the feature-flag query is still loading, a spinner is shown to avoid
// flashing the wrong route briefly.
const FeatureFlaggedLegacyRoute = (props: {
  flagKey: string;
  NewComponent: ComponentType;
  legacyPath: string;
  isNewApp?: boolean;
  serveLegacyWhenEnabled?: boolean;
  LegacyIframe: ComponentType<{ legacyPath: string; isNewApp?: boolean }>;
}) => {
  const { flagKey, NewComponent, legacyPath, isNewApp, serveLegacyWhenEnabled, LegacyIframe } = props;
  const { flags, isFetched } = useFeatureFlags();

  if (!isFetched) {
    return (
      <Box w='100%' h='calc(100vh - 100px)' display='flex' alignItems='center' justifyContent='center'>
        <BaseLoadingSpinner isLoading={true} />
      </Box>
    );
  }

  const isEnabled = flags.some((f) => f.Key === flagKey && f.Value === true);
  const showLegacy = serveLegacyWhenEnabled ? isEnabled : !isEnabled;

  return showLegacy ? <LegacyIframe legacyPath={legacyPath} isNewApp={isNewApp} /> : <NewComponent />;
};

// Factory for a lazy component that chooses between a React page and the
// legacy iframe fallback based on a per-user feature flag.
export const createFlaggedLegacyComponent = (
  flagKey: string,
  loadNewComponent: () => Promise<{ default: ComponentType }>,
  legacyPath: string,
  isNewApp: boolean | undefined,
  serveLegacyWhenEnabled?: boolean
) => {
  return async () => {
    const [{ LegacyIframe }, newModule] = await Promise.all([
      import('../components/common/LegacyIframe'),
      loadNewComponent()
    ]);
    const NewComponent = newModule.default;
    return {
      Component: () => (
        <FeatureFlaggedLegacyRoute
          flagKey={flagKey}
          NewComponent={NewComponent}
          legacyPath={legacyPath}
          isNewApp={isNewApp}
          serveLegacyWhenEnabled={serveLegacyWhenEnabled}
          LegacyIframe={LegacyIframe}
        />
      )
    };
  };
};

// Helper to create a route that branches between a React page and the legacy
// iframe fallback based on a feature flag. Intended for the "migrate a legacy
// page to React" pattern: keep the same URL, flip per-user via flag.
export const createFlaggedLegacyRoute = (args: {
  text: string;
  path: string;
  legacyPath: string;
  allowedRoles: string[];
  flagKey: string;
  loadNewComponent: () => Promise<{ default: ComponentType }>;
  isNewApp?: boolean;
  serveLegacyWhenEnabled?: boolean;
}): RouteItem => {
  return {
    text: args.text,
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path: args.path,
    allowedRoles: args.allowedRoles,
    layout: <Layout />,
    lazy: createFlaggedLegacyComponent(
      args.flagKey,
      args.loadNewComponent,
      args.legacyPath,
      args.isNewApp,
      args.serveLegacyWhenEnabled
    )
  };
};

// Helper to create legacy route items with lazy loading
export const createLegacyRoute = (
  text: string,
  path: string,
  legacyPath: string,
  allowedRoles: string[],
  subItems?: RouteItem[],
  isNewApp?: boolean
): RouteItem => {
  const linkText = isNewApp ? `${text}` : text;
  return {
    text: linkText,
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path,
    allowedRoles,
    layout: <Layout />,
    ...(subItems
      ? { subItems }
      : {
          lazy: createLegacyComponent(legacyPath, isNewApp)
        })
  };
};

export const createForcedLegacyRoute = (
  text: string,
  path: string,
  legacyPath: string,
  allowedRoles: string[],
  subItems?: RouteItem[],
  isNewApp?: boolean
): RouteItem => {
  const linkText = isNewApp ? `${text}` : text;
  return {
    text: linkText,
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path,
    allowedRoles,
    layout: <Layout />,
    ...(subItems
      ? { subItems }
      : {
          lazy: createForcedLegacyComponent(legacyPath, isNewApp)
        })
  };
};

export const createForcedLegacyLeaf = (
  text: string,
  path: string,
  legacyPath: string,
  allowedRoles: string[],
  isNewApp: boolean = false
): RouteItem => {
  const linkText = isNewApp ? `${text}` : text;
  return {
    text: linkText,
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path,
    allowedRoles,
    lazy: createForcedLegacyComponent(legacyPath, isNewApp)
  };
};

// Simple helper for leaf nodes in dropdowns
export const createLegacyLeaf = (
  text: string,
  path: string,
  legacyPath: string,
  allowedRoles: string[],
  isNewApp: boolean = false
): RouteItem => {
  const linkText = isNewApp ? `${text}` : text;
  return {
    text: linkText,
    authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
    path,
    allowedRoles,
    lazy: createLegacyComponent(legacyPath, isNewApp)
  };
};
